import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { RootAsset } from "../models/RootAsset";
import type { RootAssetWorkOrder } from "../models/RootAssetWorkOrder";
import {
	GetTaskListByArea,
	type Task,
} from "../repositorie/TaskListRepositorie";
import type { TaskListFilters } from "./useTaskListFilters";

export type TaskListAreaTable = {
	rows: RootAssetWorkOrder[];
	rootAssetName: string;
	rootAssetId: number;
};

export type TaskListData = {
	allRows: Task[];
	listRootAssets: RootAsset[];
	rootAssetsDataTables: TaskListAreaTable[];
	areasNumber: number;
};

export const TASK_LIST_QUERY_KEYS = {
	all: ["taskList"] as const,
	list: (filters: TaskListFilters) =>
		[
			"taskList",
			"list",
			filters.assetId,
			[...filters.craftIds].sort().join(","),
			filters.typeCode,
			filters.searchText,
			filters.downHours,
		] as const,
};

const EMPTY: TaskListData = {
	allRows: [],
	listRootAssets: [],
	rootAssetsDataTables: [],
	areasNumber: 0,
};

/**
 * The task list for the area in the route.
 *
 * `enabled` is what fixes #1750: with no usable area the query does not run at all, instead of
 * asking for AssetId 0, which the procedure reads as "the whole plant". The abort signal means a
 * request left behind by a filter change can no longer land last and overwrite the current one.
 */
export const useTaskList = (filters: TaskListFilters) => {
	const query = useQuery({
		queryKey: TASK_LIST_QUERY_KEYS.list(filters),
		queryFn: ({ signal }) =>
			GetTaskListByArea(
				filters.craftIds,
				filters.typeCode,
				filters.searchText,
				filters.downHours,
				filters.assetId,
				signal,
			),
		enabled: filters.assetId !== null,
		keepPreviousData: true,
		select: ({ rows, rootAssets }): TaskListData => ({
			allRows: rows,
			listRootAssets: rootAssets,
			areasNumber: rootAssets.length,
			rootAssetsDataTables: rootAssets.map((rootAsset) => ({
				rows: rows.filter(
					(row) => row.RootAssetId === rootAsset.RootAssetId,
				) as unknown as RootAssetWorkOrder[],
				rootAssetName: rootAsset.TableName,
				rootAssetId: rootAsset.RootAssetId as number,
			})),
		}),
	});

	return {
		data: query.data ?? EMPTY,
		// `isFetching`, not `isLoading`: with keepPreviousData the previous rows stay on screen while
		// a new filter loads, and the grid should still show it is working.
		isLoading: query.isFetching,
		error: query.error as Error | null,
	};
};

/** Refreshes the task list after a write, from anywhere on the page. */
export const useRefreshTaskList = () => {
	const queryClient = useQueryClient();

	return useCallback(
		() => queryClient.invalidateQueries({ queryKey: TASK_LIST_QUERY_KEYS.all }),
		[queryClient],
	);
};
